import { useState, useEffect, useContext } from 'react';
import { Box, Grid } from '@mui/material';
import CreateIcon from '@mui/icons-material/Create';
import { useTranslation } from 'react-i18next';
import {
    AppContext,
    SttFormSelect,
    SttButton,
    SttButtonNew,
    SttPrivilegePureList,
    SttSCAdminList,
} from '../../../sporttia/all';
import RoleDialog from './RoleDialog';
import {
    updateElementInArray,
    deleteElementFromArray,
} from '../../../lib/utils';
import { PERMISSIONS } from '../../../lib/permissions';

type SCAdmin = {
    id: number;
    name: string;
    login: string;
};

type Privilege = {
    id: number;
    name: string;
    code?: string;
    sport?: {
        id: number;
    };
};

type Role = {
    id?: number | string;
    name?: string;
    description?: string;
    admins?: SCAdmin[];
    privileges?: Privilege[] | unknown;
    trash?: boolean;
};

type RolesData = {
    count: number;
    rows: Role[];
};

export default function RolesTab(): React.ReactElement {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const [roles, setRoles] = useState<RolesData>();
    const [selectedRole, setSelectedRole] = useState<Role | null>(null);
    const [roleDialog, setRoleDialog] = useState<
        Role | Record<string, never>
    >();
    /**
     * Load admin roles
     */
    const loadAdminRoles = () => {
        cxt?.api('GET', `/scs/${cxt?.sc?.id}/roles`, {
            success: (r: RolesData) => {
                setRoles(r);
                if (r.count > 0) setSelectedRole(r.rows[0] || null);
            },
        });
    };

    /**
     * SC changes
     */
    useEffect(() => {
        if (cxt?.sc?.id) loadAdminRoles();
    }, [cxt?.sc?.id]);

    /**
     * Add admin to role
     */
    const addAdminToRole = (admin: SCAdmin) => {
        cxt?.api('POST', `/scs/roles/${selectedRole?.id}/admins`, {
            params: {
                idAdmin: admin.id,
            },
            success: () => {
                if (selectedRole) {
                    const updated = {
                        ...selectedRole,
                        admins: updateElementInArray(
                            selectedRole.admins || [],
                            admin,
                        ),
                    };
                    setSelectedRole(updated);
                    setRoles({
                        ...roles!,
                        rows: updateElementInArray(roles!.rows, updated),
                    });
                }
            },
        });
    };

    /**
     * Delete admin from role
     */
    const delAdminFromRole = (admin: SCAdmin) => {
        cxt?.api(
            'DELETE',
            `/scs/roles/${selectedRole?.id}/admins/${admin.id}`,
            {
                success: () => {
                    if (selectedRole) {
                        const updated = {
                            ...selectedRole,
                            admins: deleteElementFromArray(
                                selectedRole.admins || [],
                                admin,
                            ),
                        };
                        setSelectedRole(updated);
                        setRoles({
                            ...roles!,
                            rows: updateElementInArray(roles!.rows, updated),
                        });
                    }
                },
            },
        );
    };

    /**
     * Add privilege to role
     */
    const addPrivilegeToRole = (priv: Privilege) => {
        cxt?.api(
            'POST',
            `/scs/roles/${selectedRole?.id}/privileges/${priv.id}`,
            {
                success: () => {
                    if (selectedRole) {
                        const currentPrivileges = (
                            Array.isArray(selectedRole.privileges)
                                ? selectedRole.privileges
                                : []
                        ) as Privilege[];
                        const updated = {
                            ...selectedRole,
                            privileges: updateElementInArray(
                                currentPrivileges,
                                priv,
                            ),
                        };
                        setSelectedRole(updated);
                        setRoles({
                            ...roles!,
                            rows: updateElementInArray(roles!.rows, updated),
                        });
                    }
                },
            },
        );
    };

    /**
     * Delete privilege from role
     */
    const delPrivilegeFromRole = (priv: Privilege) => {
        cxt?.api(
            'DELETE',
            `/scs/roles/${selectedRole?.id}/privileges/${priv.id}`,
            {
                success: () => {
                    if (selectedRole) {
                        const currentPrivileges = (
                            Array.isArray(selectedRole.privileges)
                                ? selectedRole.privileges
                                : []
                        ) as Privilege[];
                        const updated = {
                            ...selectedRole,
                            privileges:
                                deleteElementFromArray(
                                    currentPrivileges,
                                    priv,
                                ) || [],
                        };
                        setSelectedRole(updated);
                        setRoles({
                            ...roles!,
                            rows: updateElementInArray(roles!.rows, updated),
                        });
                    }
                },
            },
        );
    };

    // -----| Render |-----
    return (
        <>
            <Box>
                <Grid container spacing={3}>
                    {roles && (
                        <SttFormSelect
                            grid
                            md={6}
                            sm={6}
                            xs={12}
                            caption={t('Roles')}
                            defVal={selectedRole && selectedRole.id}
                            onChange={({
                                value,
                            }: {
                                name: string | undefined;
                                value: unknown;
                            }) =>
                                setSelectedRole(
                                    roles.rows.find(
                                        (e: any) => e.id === value,
                                    ) || null,
                                )
                            }
                            options={roles.rows.map((role: any) => ({
                                caption: role.name,
                                value: role.id,
                            }))}
                        />
                    )}

                    <Grid
                        size={{ xs: 12, sm: 6, md: 6 }}
                        className="flex gap-3"
                    >
                        <SttButton
                            startIcon={<CreateIcon />}
                            caption={t('Modify')}
                            onClick={() =>
                                setRoleDialog(selectedRole || undefined)
                            }
                        />
                        <SttButtonNew
                            onClick={() => setRoleDialog({})}
                            permission={PERMISSIONS.ADMINISTRATION.CREATE}
                        />
                    </Grid>
                </Grid>
            </Box>

            {selectedRole && (
                <Box mt={3}>
                    <Grid container spacing={3}>
                        <Grid size={{ md: 6 }}>
                            <SttSCAdminList
                                scAdmins={selectedRole.admins || []}
                                onDelete={delAdminFromRole}
                                onAdd={addAdminToRole}
                            />
                        </Grid>

                        <Grid size={{ md: 6 }}>
                            <SttPrivilegePureList
                                privileges={
                                    (Array.isArray(selectedRole.privileges)
                                        ? selectedRole.privileges
                                        : []) as Privilege[]
                                }
                                scope="SC"
                                onAdd={addPrivilegeToRole}
                                onDelete={delPrivilegeFromRole}
                            />
                        </Grid>
                    </Grid>
                </Box>
            )}

            <RoleDialog
                role={(roleDialog as any) || null}
                onClose={(newRole: any) => {
                    // Close popup
                    setRoleDialog(undefined);

                    // Update roles
                    if (newRole && roles) {
                        // Role deleted
                        if (newRole.trash) {
                            if (roles.rows.length > 1) {
                                setSelectedRole(roles.rows[0] || null);
                            }
                            const deletedRows = deleteElementFromArray(
                                roles.rows,
                                newRole,
                            );
                            setRoles({
                                ...roles,
                                rows: deletedRows || [],
                            });

                            // Role created or updated
                        } else {
                            setSelectedRole(newRole);
                            setRoles({
                                ...roles,
                                rows: updateElementInArray(roles.rows, newRole),
                            });
                        }
                    }
                }}
            />
        </>
    );
}
